import { useState } from "react";
import { Link } from "react-router-dom";
import { CalendarClock } from "lucide-react";
import { Card } from "@/design-system/components/ui";
import { cn } from "@/design-system/lib/utils";
import { useUpcomingEvents } from "@/hooks/capitalMarkets/useDashboard";
import type { UpcomingEventDto } from "@/types/capitalMarketsDashboard";
import { resolveCapitalMarketsRoute } from "./cmEntities";
import { formatDate } from "../shared/format";

const WINDOWS = [7, 30, 90] as const;

const EVENT_LABELS: Record<string, string> = {
  DistributionDue: "Distribution",
  IdupReviewDeadline: "IDUP Review",
  CertificationExpiry: "Cert Expiry",
};

/**
 * SPV Pool v1.0 §4.7 — chronological upcoming-events list with a 7 / 30 / 90
 * day lookahead toggle.
 */
export function UpcomingEventsList() {
  const [daysAhead, setDaysAhead] = useState<number>(30);
  const query = useUpcomingEvents(daysAhead);
  const events = query.data ?? [];

  return (
    <Card>
      <div className="flex items-center justify-between gap-space-2 mb-space-3">
        <div className="flex items-center gap-space-2">
          <CalendarClock
            size={16}
            strokeWidth={1.5}
            className="text-gold-shadow"
            aria-hidden
          />
          <h2 className="font-display font-bold text-h3 m-0">
            Upcoming Events
          </h2>
        </div>
        <div className="flex items-center gap-space-1">
          {WINDOWS.map((w) => (
            <button
              key={w}
              type="button"
              onClick={() => setDaysAhead(w)}
              className={cn(
                "px-space-2 py-[2px] rounded-full text-caption font-display uppercase tracking-wide border",
                daysAhead === w
                  ? "bg-gold text-onyx border-gold font-semibold"
                  : "bg-white-pure text-gray-mid border-border-light hover:text-onyx",
              )}
            >
              {w}d
            </button>
          ))}
        </div>
      </div>

      {query.isLoading && (
        <p className="text-small text-gray-mid italic m-0">Loading…</p>
      )}
      {query.isError && (
        <p className="text-small text-status-error m-0">
          Could not load upcoming events.
        </p>
      )}
      {!query.isLoading && !query.isError && events.length === 0 && (
        <p className="text-small text-gray-mid m-0">
          Nothing scheduled in the next {daysAhead} days.
        </p>
      )}

      <ul className="m-0 p-0 list-none flex flex-col">
        {events.map((e, i) => (
          <EventRow key={`${e.eventType}-${e.entityId}-${e.date}-${i}`} event={e} />
        ))}
      </ul>
    </Card>
  );
}

function EventRow({ event }: { event: UpcomingEventDto }) {
  return (
    <li className="border-t border-border-light first:border-t-0 py-space-2">
      <div className="flex items-start gap-space-3">
        <span className="shrink-0 inline-flex flex-col items-center justify-center w-14 rounded-md bg-white-warm border border-border-light py-space-1">
          <span className="text-caption text-gray-mid font-display uppercase tracking-label">
            {formatDate(event.date)}
          </span>
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-space-2 flex-wrap">
            <span className="inline-flex items-center px-space-2 py-[2px] rounded-sm bg-onyx text-gold font-display text-[10px] font-bold uppercase tracking-[0.16em]">
              {EVENT_LABELS[event.eventType] ?? event.eventType}
            </span>
            <Link
              to={resolveCapitalMarketsRoute(event.entityType, event.entityId)}
              className="text-small font-semibold hover:text-gold-shadow truncate"
            >
              {event.entityName}
            </Link>
          </div>
          <p className="text-caption text-gray-mid m-0 mt-[2px]">
            {event.description}
          </p>
        </div>
      </div>
    </li>
  );
}
